import { ModelRow } from "@mcleod/core";
import {Image, Label, TableCell, TableRowDisplayEvent} from "@mcleod/components";
import { FilterPanel, SecondaryFilterProps } from "@mcleod/dispatch";
import { AutogenLayoutOrderPayablesHold } from "./autogen/AutogenLayoutOrderPayablesHold";
import { OrderPayablesTabLayout } from "./OrderPayables";
import { OrderPayablesUtil } from "./OrderPayablesUtil";
import { RowOrderPayablesProfile } from "./models/ModelOrderPayablesProfile";
import { OrderPayablesTaskFilter } from "./OrderPayablesTaskFilter";

export class OrderPayablesHold extends AutogenLayoutOrderPayablesHold implements OrderPayablesTabLayout {
    public getProfileRow: () => RowOrderPayablesProfile;
    public filterPanel: FilterPanel;
    private initialLoad: boolean = true;

    onLoad(): void | Promise<void> {
        this.filterPanel = new FilterPanel([], this.tableOrderPayables, this.panelTaskFilters);
        this.tableOrderPayables.addRowDisplayListener((event: TableRowDisplayEvent) => this.tableOrderPayablesOnRowDisplay(event));
    }

    updateTypeColumnVisibility(): void {
        const activeFilter = this.filterPanel.getActiveFilter();
        const shouldShowType = this.initialLoad || activeFilter?.dynamic === true || activeFilter == null;
        const isStaticFilter = activeFilter != null && !activeFilter.dynamic;
        const isExceptionFilter = activeFilter?.id === OrderPayablesTaskFilter.TP_EXCEPTION.id;
        const isSubmittedToTriumphPayFilter = activeFilter?.id === OrderPayablesTaskFilter.TP_SUBMITTED.id;

        // Handle type column visibility
        const typeColumn = this.tableOrderPayables.getColumnByCaption("Type");
        if (typeColumn) {
            this.tableOrderPayables.setColumnVisibility(typeColumn, shouldShowType);
        }

        // Handle Exception, Payment Status, and Payment ID column visibility - show only when static filter is active
        const exceptionColumn = this.tableOrderPayables.getColumnByCaption("Exception");
        if (exceptionColumn) {
            this.tableOrderPayables.setColumnVisibility(exceptionColumn, (isStaticFilter && isExceptionFilter));
        }

        const paymentStatusColumn = this.tableOrderPayables.getColumnByCaption("Payment Status");
        if (paymentStatusColumn) {
            this.tableOrderPayables.setColumnVisibility(paymentStatusColumn, (isStaticFilter && isSubmittedToTriumphPayFilter));
        }

        const paymentIdColumn = this.tableOrderPayables.getColumnByCaption("Payment ID");
        if (paymentIdColumn) {
            this.tableOrderPayables.setColumnVisibility(paymentIdColumn, (isStaticFilter && isSubmittedToTriumphPayFilter));
        }

        const payAdjustmentWarningColumn = this.tableOrderPayables.getColumnByCaption("columnHeaderWarning");
        if (payAdjustmentWarningColumn) {
            this.tableOrderPayables.setColumnVisibility(payAdjustmentWarningColumn, (isStaticFilter && isSubmittedToTriumphPayFilter));
        }

        // Update cells and image visibility together to ensure consistency
        this.tableOrderPayables.rows.forEach(row => {
            const typeCell = row.cells.find(c => c.id === "tableOrderPayablesTypeColumn");
            if (typeCell) {
                typeCell.visible = shouldShowType;
                const typeImage = row.findComponentById("imageRecordType") as Image;
                if (typeImage) {
                    typeImage.visible = true;
                    typeImage.marginTop = "0";
                    typeImage.marginBottom = "0";
                }
            }

            // Update Exception, Payment Status, and Payment ID cell visibility
            const exceptionCell = row.cells.find(c => c.id === "tableOrderPayablesExceptionColumn");
            if (exceptionCell) {
                exceptionCell.visible = isStaticFilter && isExceptionFilter;
                const labelException = row.findComponentById("labelException") as Label;
                if (labelException && exceptionCell.visible) {
                    labelException.visible = true;
                }
            }

            const paymentStatusCell = row.cells.find(c => c.id === "paymentStatusCell");
            if (paymentStatusCell) {
                paymentStatusCell.visible = isStaticFilter && isSubmittedToTriumphPayFilter;
                if(paymentStatusCell.visible) {
                    const labelPaymentStatus = row.findComponentById("labelPaymentStatus") as Label;
                    if (labelPaymentStatus) {
                        labelPaymentStatus.visible = true;
                    }
                }
            }

            const paymentIdCell = row.cells.find(c => c.id === "paymentIdCell");
            if (paymentIdCell) {
                paymentIdCell.visible = isStaticFilter && isSubmittedToTriumphPayFilter;
                if(paymentIdCell.visible) {
                    const labelPaymentId = row.findComponentById("labelPaymentId") as Label;
                    if (labelPaymentId) {
                        labelPaymentId.visible = true;
                    }
                }
            }

            const payAdjustmentWarningCell = row.cells.find(c => c.id === "adjustmentWarningCell");
            if (payAdjustmentWarningCell) {
                payAdjustmentWarningCell.visible = !isStaticFilter || (isStaticFilter && !isSubmittedToTriumphPayFilter);
                if(payAdjustmentWarningCell.visible) {
                    const warningColumn = this.tableOrderPayables.getColumnByCaption("columnHeaderWarning");
                    const labelPayAdjustmentWarning = row.findComponentById("imageAdjustmentWarning") as Image;
                    if (labelPayAdjustmentWarning) {
                        this.tableOrderPayables.setColumnVisibility(warningColumn, true);
                        labelPayAdjustmentWarning.visible = true;
                    }
                }else{
                    const labelPayAdjustmentWarning = row.findComponentById("imageAdjustmentWarning") as Image;
                    if (labelPayAdjustmentWarning) {
                        const warningColumn = this.tableOrderPayables.getColumnByCaption("columnHeaderWarning");
                        if (warningColumn) {
                            this.tableOrderPayables.setColumnVisibility(warningColumn, false);
                            labelPayAdjustmentWarning.visible = false;
                        }
                    }
                }
            }
        });

        // Only turn off initial load after we've handled visibility
        if (this.initialLoad && this.tableOrderPayables.rows.length > 0) {
            this.initialLoad = false;
        }
    }

    getLayoutBaseFilter(row: ModelRow): boolean {
        return row.get("ready_to_pay_flag") == "N" && !row.isNull("back_office_reason_code");
    }

    tableOrderPayablesOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();

        tableRow.forEveryChildComponent((component) => {
            OrderPayablesUtil.setTooltipContent(component, tableRow.data);
        });
        OrderPayablesUtil.setComponentDetails({
            tableRow: tableRow,
            profileRow: this.getProfileRow()
        });
    }

    removeDynamicTaskFilters(): void {
        for (const filter of this.filterPanel.filters) {
            if (filter.dynamic) {
                this.filterPanel.removeFilterFromPanel(filter);
            }
        }
    }

    refreshDynamicTaskFilters(): SecondaryFilterProps[] {
        const activeFilter = this.filterPanel.getActiveFilter();
        this.removeDynamicTaskFilters();
        const uniqueReasonCodes: Map<string, string> = new Map();
        for (const tableRow of this.tableOrderPayables.allRows) {
            const modelRow = tableRow.data as ModelRow;
            if (this.getLayoutBaseFilter(modelRow)) {
                uniqueReasonCodes.set(modelRow.get("back_office_reason_code"), modelRow.get("back_office_reason_descr"));
            }
        }
        for (const [reasonCode, reasonDesc] of uniqueReasonCodes) {
            const filter: SecondaryFilterProps = {
                id: reasonCode,
                caption: reasonDesc,
                filter: (row: ModelRow) => {
                    return row.get("back_office_reason_code") == reasonCode;
                },
                dynamic: true
            };
            this.filterPanel.addFilter(filter);
            if (activeFilter != null && activeFilter.id == reasonCode) {
                this.filterPanel.toggleFilterById(filter.id);
            }
        }
        if (activeFilter != null && !uniqueReasonCodes.has(activeFilter.id)) {
            this.filterPanel.applyRemoveFilter(activeFilter);
        }
        return this.filterPanel.filters.filter(f => f.dynamic);
    }
}
